import i18n from '@i18n';
import { appEnv, appVersion, environment, isNotProduction } from '@utils/environment';
import { config } from '@config';
import { HttpStatusCode } from '../../http-status-codes';

function httpStatusFromError(err: any): number | undefined {
    return (
        err?.networkError?.statusCode ??
        err?.networkError?.status ??
        err?.statusCode ??
        err?.status ??
        err?.response?.status ??
        err?.networkError?.response?.status ??
        undefined
    );
}

const isTimeoutError = (
    err: unknown
): err is HttpStatusCode.RequestTimeout | HttpStatusCode.GatewayTimeout => {
    return [HttpStatusCode.RequestTimeout, HttpStatusCode.GatewayTimeout].includes(
        err as HttpStatusCode
    );
};

export function humanizeError(err: any): string {
    const code = httpStatusFromError(err);
    const msg = String(err?.message || '');

    // Clearly offline
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return i18n.t('Errors.Network');
    }

    // HTTP status codes
    if (code === HttpStatusCode.Unauthorized) return i18n.t('Errors.SessionExpired');
    if (code === HttpStatusCode.Forbidden) return i18n.t('Errors.Forbidden');
    if (code === HttpStatusCode.NotFound) return i18n.t('Errors.NotFound');
    if (code === HttpStatusCode.TooManyRequests) return i18n.t('Errors.TooManyRequests');
    if (isTimeoutError(code)) return i18n.t('Errors.Timeout');
    if (code && code >= 500) return i18n.t('Errors.Server');

    // No/unknown status → fetch-level network problems (DNS/CORS/offline/aborted)
    const looksLikeNetworkFailure =
        err?.name === 'AbortError' ||
        err instanceof TypeError ||
        code === 0 ||
        /Network request failed|Failed to fetch|load failed|NetworkError/i.test(msg);

    if (looksLikeNetworkFailure) {
        return i18n.t('Errors.Network');
    }

    // GraphQL error (200 OK with errors[])
    const gqlMsg = err?.graphQLErrors?.[0]?.message;
    return gqlMsg || msg || i18n.t('Errors.Generic');
}

export type ErrorContext = {
    where?: string;
    route?: string;
    orgId?: string;
    requestId?: string;
    operation?: string;
    extra?: Record<string, unknown>;
};

const isNonProd = isNotProduction() || config.environment !== 'production';

export function logError(err: unknown, ctx: ErrorContext = {}) {
    const payload = {
        message: (err as any)?.message ?? String(err),
        name: (err as any)?.name,
        stack: (err as any)?.stack,
        ...ctx,
        env: environment(),
        appEnv: appEnv(),
        version: appVersion(),
    };

    if (isNonProd) {
        // eslint-disable-next-line no-console
        console.groupCollapsed(`%cError: ${payload.message}`, 'color:#e11d48;font-weight:bold');
        // eslint-disable-next-line no-console
        console.log(payload);
        // eslint-disable-next-line no-console
        console.groupEnd();
    } else {
        // Sentry.captureException(err instanceof Error ? err : new Error(payload.message), { extra: payload });
    }
}
